import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import { DataTable, FilterBar, SearchInput, PaginationControls, StatsCard, ConfirmModal } from '../../../components/admin/shared';
import { Users, UserPlus, Download, Mail, Shield, UserX, UserCheck } from 'lucide-react';
import api from '../../../api/educentialApi';

const UsersList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, students: 0, educators: 0, admins: 0 });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filters, setFilters] = useState({
    role: searchParams.get('role') || '',
    status: searchParams.get('status') || '',
    search: searchParams.get('search') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || ''
  });

  // Sorting
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Bulk operations
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkOperation, setBulkOperation] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, itemsPerPage, filters, sortBy, sortOrder]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        ...filters
      };

      const response = await api.get('/admin/users', { params });
      setUsers(response.data.users);
      setTotalPages(response.data.pagination.totalPages);

      // Update stats
      setStats({
        total: response.data.pagination.total,
        students: response.data.users.filter(u => u.role === 'STUDENT').length,
        educators: response.data.users.filter(u => u.role === 'EDUCATOR').length,
        admins: response.data.users.filter(u => u.role === 'ADMIN').length
      });
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);

    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(name, value);
    } else {
      newParams.delete(name);
    }
    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    setFilters({
      role: '',
      status: '',
      search: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchParams({});
    setCurrentPage(1);
  };

  const handleSort = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
  };

  const handleRowClick = (user) => {
    navigate(`/admin/users/${user.id}`);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const handleBulkOperation = async (operation) => {
    setBulkOperation(operation);
    setShowBulkModal(true);
  };

  const executeBulkOperation = async () => {
    try {
      const operationData = {
        userIds: selectedUsers,
        operation: bulkOperation
      };

      if (bulkOperation === 'notify') {
        // For now, use a simple notification
        operationData.data = {
          notification: {
            title: 'Admin Notification',
            message: 'You have a new message from the admin team.',
            type: 'info'
          }
        };
      }

      await api.post('/admin/users/bulk', operationData);
      setShowBulkModal(false);
      setSelectedUsers([]);
      fetchUsers();

      alert(`Bulk operation "${bulkOperation}" completed successfully for ${selectedUsers.length} users.`);
    } catch (error) {
      console.error('Error executing bulk operation:', error);
      alert('Failed to execute bulk operation. Please try again.');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.post('/admin/users/bulk', {
        userIds: selectedUsers.length > 0 ? selectedUsers : users.map(u => u.id),
        operation: 'export'
      });

      // Convert to CSV and download
      const csvContent = convertToCSV(response.data.data);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (error) {
      console.error('Error exporting users:', error);
      alert('Failed to export users. Please try again.');
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];

    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        return typeof value === 'string' ? `"${value}"` : value;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={selectedUsers.length === users.length && users.length > 0}
          onChange={toggleSelectAll}
          className="rounded border-gray-300"
        />
      ),
      render: (user) => (
        <input
          type="checkbox"
          checked={selectedUsers.includes(user.id)}
          onChange={() => toggleUserSelection(user.id)}
          onClick={(e) => e.stopPropagation()}
          className="rounded border-gray-300"
        />
      )
    },
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (user) => <span className="font-mono text-xs">{user.id}</span>
    },
    {
      key: 'fullName',
      label: 'Name',
      sortable: true,
      render: (user) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
            <span className="text-xs font-medium text-gray-600">
              {user.fullName?.charAt(0) || user.email.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900">{user.fullName || 'N/A'}</div>
            <div className="text-xs text-gray-500">{user.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (user) => {
        const colors = {
          ADMIN: 'bg-purple-100 text-purple-800',
          EDUCATOR: 'bg-blue-100 text-blue-800',
          STUDENT: 'bg-green-100 text-green-800'
        };
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[user.role]}`}>
            {user.role}
          </span>
        );
      }
    },
    {
      key: 'applicationStatus',
      label: 'Status',
      sortable: true,
      render: (user) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          user.applicationStatus
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {user.applicationStatus ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'createdAt',
      label: 'Joined',
      sortable: true,
      render: (user) => (
        <div className="text-sm">
          <div>{new Date(user.createdAt).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500">{new Date(user.createdAt).toLocaleTimeString()}</div>
        </div>
      )
    },
    {
      key: 'lastLoginAt',
      label: 'Last Login',
      sortable: true,
      render: (user) => user.lastLoginAt ? (
        <div className="text-sm">
          <div>{new Date(user.lastLoginAt).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500">{new Date(user.lastLoginAt).toLocaleTimeString()}</div>
        </div>
      ) : (
        <span className="text-gray-400 text-sm">Never</span>
      )
    }
  ];

  const filterConfig = [
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      value: filters.role,
      options: [
        { value: 'STUDENT', label: 'Student' },
        { value: 'EDUCATOR', label: 'Educator' },
        { value: 'ADMIN', label: 'Admin' }
      ]
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      value: filters.status,
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    },
    {
      name: 'dateFrom',
      label: 'From Date',
      type: 'date',
      value: filters.dateFrom
    },
    {
      name: 'dateTo',
      label: 'To Date',
      type: 'date',
      value: filters.dateTo
    }
  ];

  return (
    <AdminLayout title="User Management">
      <div className="p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatsCard title="Total Users" value={stats.total} icon={Users} loading={loading} />
          <StatsCard title="Students" value={stats.students} icon={UserCheck} loading={loading} />
          <StatsCard title="Educators" value={stats.educators} icon={UserCheck} loading={loading} />
          <StatsCard title="Admins" value={stats.admins} icon={Shield} loading={loading} />
        </div>

        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex-1 max-w-md">
            <SearchInput
              value={filters.search}
              onChange={(value) => handleFilterChange('search', value)}
              placeholder="Search by name or email..."
            />
          </div>
          <button
            onClick={() => navigate('/admin/users/create')}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add User</span>
          </button>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filterConfig}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
        />

        {/* Bulk Actions Bar */}
        {selectedUsers.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleExport}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 text-sm"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
                <button
                  onClick={() => handleBulkOperation('notify')}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 text-sm"
                >
                  <Mail className="w-4 h-4" />
                  <span>Notify</span>
                </button>
                <button
                  onClick={() => handleBulkOperation('deactivate')}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 text-sm"
                >
                  <UserX className="w-4 h-4" />
                  <span>Deactivate</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <DataTable
          columns={columns}
          data={users}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onRowClick={handleRowClick}
          loading={loading}
          emptyMessage="No users found. Try adjusting your filters."
        />

        {/* Pagination */}
        {!loading && users.length > 0 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}

        {/* Bulk Operation Confirmation Modal */}
        <ConfirmModal
          isOpen={showBulkModal}
          title={`Confirm Bulk ${bulkOperation}`}
          message={`Are you sure you want to ${bulkOperation} ${selectedUsers.length} user(s)?`}
          confirmText="Confirm"
          cancelText="Cancel"
          onConfirm={executeBulkOperation}
          onCancel={() => setShowBulkModal(false)}
          dangerous={bulkOperation === 'deactivate' || bulkOperation === 'delete'}
        />
      </div>
    </AdminLayout>
  );
};

export default UsersList;
